import React, { useRef, useContext, useState, useEffect, useCallback } from "react";
import pencil from "../../assets/pencil.png";
import trash from "../../assets/trash.png";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";
import { toLowercaseKeys } from "../../helpers/toLowerCaseKeys";
import { Dropdown } from 'react-bootstrap';
import { moduleListService } from "../../services/module-list.service";
import { authenticationService } from "../../services/authentication.service";
import { SetParentMenuEdit, SetParentMenu } from "../../store/parentMenuAtom";
import ParentMenuSideBar from "./ParentMenuSideBar";


const InfoRow = ({ label, value }) => (
    <div>
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800">{value || "—"}</p>
    </div>
);


const ParentMenuDetailsPage = () => {
    const setParentMenuEdit = SetParentMenuEdit();
    const setParentMenuList = SetParentMenu();
    const myPromiseModal = createModal(GeneralModal);

    const { id } = useParams();
    const navigate = useNavigate();
    const logicContext = useContext(LogicContext);

    const [moduleData, setModuleData] = useState({ menu_parent_info: [], action_status: [] });

    const { pathname } = useLocation();
    const printRef = useRef(null);
    const url = pathname.split("/");
    const currModuleId = url[1];
    const currModuleName = url[2];
    const currUserMenu = url[3];

    useEffect(() => {
        document.title = "Parent Menu Details";
        loadDetailsData();
    }, [id]);

    const loadDetailsData = () => {
        moduleListService.getParentMenuListForSingleModule(id).then((response) => {
            const serverData = toLowercaseKeys(response.data);
            if (serverData) setModuleData(serverData);
        }).catch(err => {
            console.error("Failed to load details", err);
            logicContext.showToast("Failed to load details", "error");
        });
    };

    const editCurrentData = async () => {
        const response = await myPromiseModal({
            title: "Modify Parent Menu",
            body: "Do you want to modify this parent menu?",
            showInput: false,
        });
        if (response.decision === true) {
            setParentMenuEdit(current);
            navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
        }
    };

    const toggleStatus = async () => {
        const newStatus = isActive ? 'I' : 'A';
        const label = isActive ? 'Deactivate' : 'Activate';
        const confirmed = await myPromiseModal({
            title: `${label} Parent Menu`,
            body: `Do you want to ${label.toLowerCase()} "${current?.menu_parent_name}"?`,
            showInput: false,
        });
        if (!confirmed?.decision) return;

        try {
            const authUser = authenticationService.currentUserObject();
            const payload = {
                module_id: Number(current.module_id),
                menu_parent_name: current.menu_parent_name,
                menu_parent_print_sequence: Number(current.menu_parent_print_sequence),
                menu_parent_status: newStatus,
                user_id: authUser?.User_Id,
                option: 2,
                menu_parent_id: current.menu_parent_id,
            };
            const response = await moduleListService.saveParentMenu(payload);
            const result = logicContext.httpResponseManager(
                response.data?.[0] || response.data?.result_status?.[0]
            );
            if (result.message_id === 1) {
                const applyStatus = (item) =>
                    String(item.menu_parent_id) === String(id)
                        ? { ...item, status_id: newStatus, status_name: newStatus === 'A' ? 'Active' : 'Inactive' }
                        : item;

                setModuleData(prev => ({
                    ...prev,
                    menu_parent_info: prev.menu_parent_info.map(applyStatus),
                }));

                // keep the list page / sidebar atom in sync — same menu_parent_info shape
                setParentMenuList(prev => ({
                    ...prev,
                    menu_parent_info: (prev?.menu_parent_info || []).map(applyStatus),
                }));

                logicContext.showToast(result.message_text || `Parent Menu ${label.toLowerCase()}d successfully!`, "success");
            } else {
                logicContext.showToast(result.message_text || "Operation failed", "error");
            }
        } catch (err) {
            console.error(err);
            logicContext.showToast("Failed to update status", "error");
        }
    };

    const bulkActionsSingle = async (action) => {
        const result = await myPromiseModal({
            title: 'Confirm your action!',
            body: `Do you want to ${action.status_name}?`,
            showInput: false,
        });
        if (!result.decision) return;

        try {
            const payload = { option: action.action_option, ref_no: id };
            // const res = await moduleListService.moduleAction(payload);
        } catch (error) {
            console.error("Action failed", error);
        }
    };

    const viewItemDetails = useCallback((itemId) => {
        navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${itemId}`);
    }, [currModuleId, currModuleName, currUserMenu, navigate]);

    // The API returns menu_parent_info — find the item matching the URL id
    const current = moduleData?.menu_parent_info?.find(
        item => String(item.menu_parent_id) === String(id)
    ) ?? moduleData?.menu_parent_info?.[0];
    const isActive = current?.status_name === 'Active' || current?.status_id === 'A';
    const initials = current?.menu_parent_name?.charAt(0)?.toUpperCase() || "M";

    return (
        <section className="mt-4 w-full mx-auto px-4 sm:px-6 lg:px-8">

            {/* ── Action Buttons ── */}
            <div className="flex justify-end gap-3 mb-6">
                {moduleData?.action_status?.length > 0 && (
                    <Dropdown>
                        <Dropdown.Toggle
                            className="text-sm text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition shadow-sm flex items-center gap-2"
                            id="dropdown-actions"
                        >
                            Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="mt-2 shadow-xl border-none rounded-xl bg-white py-0 overflow-hidden">
                            {moduleData.action_status.map((option, i) => (
                                <Dropdown.Item
                                    key={i}
                                    onClick={() => bulkActionsSingle(option)}
                                    className="hover:bg-blue-50 py-2.5 px-4 text-gray-700 text-sm border-b border-gray-50 last:border-0 transition-colors"
                                >
                                    {option?.status_name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                <button
                    onClick={editCurrentData}
                    className="p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition"
                    title="Edit"
                >
                    <img src={pencil} alt="Edit" className="w-5 h-5" />
                </button>

                <button
                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition opacity-50 cursor-not-allowed"
                    title="Delete (not implemented)"
                    disabled
                >
                    <img src={trash} alt="Delete" className="w-5 h-5" />
                </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">

                {/* ── Sidebar ── */}
                <div className="flex-shrink-0">
                    <ParentMenuSideBar viewItemDetails={viewItemDetails} />
                </div>

                {/* ── Detail Card ── */}
                <div ref={printRef} className="flex-1">
                    <div className="bg-white shadow-sm rounded-2xl overflow-hidden border border-gray-100">

                        {/* Banner */}
                        <div className="h-28 bg-gradient-to-r from-blue-600 to-indigo-600 relative">
                            <div className="absolute -bottom-10 left-8 flex items-end gap-5">
                                <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center">
                                    <div className="w-full h-full bg-blue-50 rounded-2xl flex items-center justify-center">
                                        <span className="text-3xl font-bold text-blue-600">{initials}</span>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h1 className="text-xl font-bold text-white drop-shadow">
                                        {current?.menu_parent_name || "Parent Menu Details"}
                                    </h1>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white font-medium">
                                            ID: {current?.menu_parent_id || "—"}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold border
                                            ${isActive
                                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                                : 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                                            {isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8 pt-16">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                {/* Parent Menu Details */}
                                <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 space-y-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        Parent Menu Details
                                    </h3>
                                    <InfoRow label="Menu Parent ID" value={current?.menu_parent_id} />
                                    <InfoRow label="Menu Parent Name" value={current?.menu_parent_name} />
                                    <InfoRow label="Print Sequence" value={current?.menu_parent_print_sequence} />
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Status</p>
                                        <button
                                            onClick={toggleStatus}
                                            className="flex items-center gap-2.5"
                                            title={isActive ? 'Click to deactivate' : 'Click to activate'}
                                        >
                                            <div className={`relative w-10 h-5 rounded-full transition-colors duration-200
                                                ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                                                    ${isActive ? 'translate-x-5' : 'translate-x-0'}`} />
                                            </div>
                                            <span className={`text-xs font-semibold ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                {isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                {/* Module Details */}
                                <div className="bg-gray-50 rounded-xl border border-gray-100 p-5 space-y-5">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                                        Module Details
                                    </h3>
                                    <InfoRow label="Module ID" value={current?.module_id} />
                                    <InfoRow label="Module Name" value={current?.module_name} />
                                    <div>
                                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Module</p>
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                            {current?.module_name || "—"}
                                        </span>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ParentMenuDetailsPage;
